import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi } from '../services/api';
import { clearSession, SESSION_EXPIRED_EVENT } from '../services/authSession';

const AuthContext = createContext(null);

const redirectToLogin = () => {
  window.history.replaceState({}, '', '/login');
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setSessionExpired(true);
      redirectToLogin();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    const restore = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const current = await authApi.me();
        if (localStorage.getItem('token') === token) setUser(current);
      } catch {
        if (localStorage.getItem('token') === token) clearSession();
      } finally {
        setLoading(false);
      }
    };
    restore();
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const storeSession = async (result) => {
    // ID tokens carry custom:tenant_id; access tokens do not.
    const token = result.id_token || result.access_token;
    localStorage.setItem('token', token);
    if (result.refresh_token) {
      localStorage.setItem('refresh_token', result.refresh_token);
    }
    const current = await authApi.me();
    localStorage.setItem('user', JSON.stringify(current));
    setUser(current);
    setSessionExpired(false);
    return result;
  };

  const login = async (username, password) => {
    const result = await authApi.login(username, password);
    if (result.mfa_required || result.mfa_setup_required || result.new_password_required) {
      return result;
    }
    return storeSession(result);
  };

  const completePassword = async (username, session, newPassword) => {
    const result = await authApi.completePassword(username, session, newPassword);
    if (result.mfa_required || result.mfa_setup_required || result.new_password_required) {
      return result;
    }
    return storeSession(result);
  };

  const forgotPassword = (username) => authApi.forgotPassword(username);

  const refreshSession = useCallback(async () => {
    const previousToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!previousToken || !refreshToken) throw new Error('Please sign in again to refresh your plan.');
    const result = await authApi.refresh();
    if (localStorage.getItem('token') !== previousToken || localStorage.getItem('refresh_token') !== refreshToken) {
      throw new Error('Your session changed. Please retry.');
    }
    if (!result.id_token) throw new Error('Authentication did not return refreshed account claims.');
    const current = await authApi.meWithToken(result.id_token);
    if (localStorage.getItem('token') !== previousToken || localStorage.getItem('refresh_token') !== refreshToken) {
      throw new Error('Your session changed. Please retry.');
    }
    localStorage.setItem('token', result.id_token);
    if (result.refresh_token) localStorage.setItem('refresh_token', result.refresh_token);
    localStorage.setItem('user', JSON.stringify(current));
    setUser(current);
    return current;
  }, []);

  const confirmForgotPassword = (username, code, newPassword) =>
    authApi.confirmForgotPassword(username, code, newPassword);

  const logout = () => {
    clearSession();
    setUser(null);
    setSessionExpired(false);
    redirectToLogin();
  };

  return (
    <AuthContext.Provider value={{ user, loading, sessionExpired, login, completePassword, forgotPassword, confirmForgotPassword, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
